import { HistoryLocation } from '@gatsbyjs/reach-router';
import { omit } from 'lodash';

import { Country } from '@skippay/core/@types/country';

import { GA_EVENT, GA_PAGEVIEW } from './constants';
import {
	GA4Event,
	GA4Pageview,
	GAEvent,
	GAEventData,
	GAPageview,
	GAPageviewData,
	GA_VERSION,
} from './types';

function eventCallback(): void {
	window.dataLayer.push({ gaEventData: undefined });
}

export const enhanceEvent = (
	eventData: GAEventData | GA4Event,
	eventExtra?: { country: Country } & Record<string, unknown>
): GAEvent | GA4Event => {
	if (eventData.gaVersion === GA_VERSION.V4) {
		return {
			event: eventData.eAct ? `${eventData.eCat} - ${eventData.eAct}` : eventData.eCat,
			...eventExtra,
			eInt: false,
			eVal: 1,
			...omit(eventData, 'gaVersion'),
		} as GA4Event;
	}

	return {
		eventCallback,
		...eventExtra,
		gaEventData: {
			eInt: false,
			eVal: 1,
			...omit(eventData, 'gaVersion'),
		} as GAEventData,
		event: GA_EVENT,
	};
};

export const enhancePageview = (
	pageview: (GAPageviewData | GA4Pageview) & { country: Country },
	previousLocation?: HistoryLocation | null
): GAPageview | GA4Pageview => {
	const pUrl =
		pageview.pUrl || `${window.location.pathname}${window.location.search}${window.location.hash}`;

	const previousRoute = previousLocation
		? `${previousLocation.pathname}${previousLocation.search}`
		: undefined;

	if (pageview.gaVersion === GA_VERSION.V4) {
		return {
			event: 'pageview',
			page_location: pUrl,
			page_title: pageview.pTitle,
			...omit(pageview, ['gaVersion', 'pHostname', 'event', 'pUrl', 'pTitle']),
			...(!!previousRoute && { page_referrer: previousRoute }),
		} as GA4Pageview;
	}

	return {
		...omit(pageview, 'gaVersion'),
		event: GA_PAGEVIEW,
		pHostname: pageview.pHostname || 'skippay.cz',
		pUrl,
		...(!!previousRoute && { page_referrer: previousRoute }),
	} as GAPageview;
};

/**
 * When the GoogleAnalytics are not initialized, it does nothing.
 */
export const pushNonEmptyToDataLayer = (
	obj: GAEvent | GA4Event | GAPageview | GA4Pageview
): void => {
	if (obj) {
		window.dataLayer?.push(obj);
	}
};
